/**
 * InfiniteBoard — the full board, composed from the substrate primitives:
 * a `BoardProvider` (shared per-board runtime), a bare `BoardSurface` (chrome-free
 * renderer + minimap), and a `BoardChromeSlot` (breadcrumb + pop/home/help +
 * smart-view). A convenience composition carrying the historical props + ref
 * handle so existing callers are untouched; surfaces that want different chrome —
 * or none, an embed — compose the same primitives directly.
 *
 * InfiniteBoard reads NO board context itself, so it re-renders only on its own
 * prop change and never recreates the board element on a focal/pan/zoom frame
 * (the substrate re-renders instead, reaching only the chrome). The diff
 * visualizer skips this wrapper and composes `BoardCanvas` directly.
 */
import { forwardRef, useImperativeHandle, type ReactNode } from "react";
import { BoardProvider } from "./BoardProvider";
import { BoardSurface } from "./BoardSurface";
import { BoardChromeSlot } from "./BoardChromeSlot";
import { useBoardCommands } from "./BoardContext";
import { registerBuiltinWidgets } from "./builtinWidgets";
import type { BoardCanvasProps } from "./BoardCanvas";
import type { OverflowItem } from "../components/OverflowToolbar";
import type { Viewport } from "@archlang/scene/board";
import type { ExportFocalOptions } from "./exportPng";

// The board owns its widgets: register the built-ins (arch-module, …) eagerly at
// module load — BEFORE any board renders — so node tags mount already-upgraded.
// (Registering in an effect, after first render, leaves a window where tags are
// rendered as undefined elements and lose attrs like the description on upgrade.)
// Idempotent + guarded for SSR (no customElements/document there).
if (typeof window !== "undefined") registerBuiltinWidgets();

export interface InfiniteBoardProps extends BoardCanvasProps {
  /**
   * Make the camera operator ("smart view") available: shows the follow-edits
   * toggle in the chrome and lets the user turn it on. The board owns the on/off
   * state (persisted); the host only opts the surface in. Off (no toggle) for
   * read-only embeds.
   */
  readonly operator?: boolean;
  /** Force smart-view permanently on with NO toggle — for a showcase (e.g. the
   *  site demo) where the camera should always follow edits. Ignores the
   *  persisted preference and hides the toggle. Implies the operator surface. */
  readonly operatorForced?: boolean;
  /** Host content rendered before the breadcrumb in the board chrome (the
   *  viewer's nav-panel toggle). Opaque to the board — board ⊥ sidebars. */
  readonly chromeLeading?: ReactNode;
  /** Action buttons (the viewer's view controls) placed in the top bar's
   *  trailing slot. The board appends its own "smart view" toggle to these so
   *  they collapse together into a "=" dropdown when the breadcrumb needs room. */
  readonly chromeTrailingItems?: readonly OverflowItem[];
  /** Phone chrome (`mobile` on BoardCanvasProps flips the minimap too):
   *  host settings for the full-screen board menu. Render prop gets `close`
   *  so applying a pick dismisses the menu. */
  readonly mobileMenu?: (close: () => void) => ReactNode;
  /** Mobile header search button → host's ⌘F modal. Absent → no button. */
  readonly onMobileSearch?: () => void;
}

export const InfiniteBoard = forwardRef<InfiniteBoardHandle, InfiniteBoardProps>(function InfiniteBoard(
  { operator = false, operatorForced = false, chromeLeading, chromeTrailingItems, mobileMenu, onMobileSearch, ...props },
  ref,
) {
  const mobile = props.mobile ?? false;
  // The substrate gets root + viewport wiring; the bare surface gets the rest of
  // the board-render props (its operatorEnabled/operatorInteractive come from the
  // substrate, overriding any that rode in on the spread).
  const { root, onViewportChange, ...surfaceProps } = props;
  return (
    <BoardProvider
      root={root}
      operator={operator}
      operatorForced={operatorForced}
      initialViewport={props.initialViewport}
      onViewportChange={onViewportChange}
    >
      <div className="relative w-full h-full">
        <BoardSurface {...surfaceProps} />
        <BoardChromeSlot
          mobile={mobile}
          leading={chromeLeading}
          trailingItems={chromeTrailingItems}
          mobileMenu={mobileMenu}
          onMobileSearch={onMobileSearch}
        />
        <InfiniteBoardHandleBridge ref={ref} />
      </div>
    </BoardProvider>
  );
});

/** Bridges the host forwardRef to the substrate's command runtime. Reads ONLY the
 *  stable commands context (never focal) and renders nothing, so it never
 *  re-renders on a breadcrumb change. */
const InfiniteBoardHandleBridge = forwardRef<InfiniteBoardHandle>(function InfiniteBoardHandleBridge(_props, ref) {
  const { commands, getViewport } = useBoardCommands();
  useImperativeHandle(
    ref,
    () => ({
      flyTo: commands.flyTo,
      enterSpace: commands.enterSpace,
      reframe: commands.reframe,
      framePair: commands.framePair,
      getViewport,
      restoreViewport: commands.restoreViewport,
      setViewportImmediate: commands.setViewportImmediate,
      exportFocalAsPng: commands.exportFocalAsPng,
      exportFocalAsSvg: commands.exportFocalAsSvg,
      copyFocalToClipboard: commands.copyFocalToClipboard,
    }),
    [commands, getViewport],
  );
  return null;
});

export interface InfiniteBoardHandle {
  flyTo(moduleId: string): void;
  /** Navigate INTO a space by id (focal becomes the space itself). */
  enterSpace(moduleId: string): void;
  /** Re-fit the current focal to the container's current size after a host-
   *  driven reshape (e.g. switching the editor/board split orientation). */
  reframe(): void;
  /** Frame a connection's two endpoint nodes together (scrubber locate). */
  framePair(aId: string, bId: string): void;
  getViewport(): Viewport;
  restoreViewport(vp: Viewport): void;
  setViewportImmediate(vp: Viewport): void;
  exportFocalAsPng(opts?: ExportFocalOptions): Promise<void>;
  exportFocalAsSvg(opts?: ExportFocalOptions): Promise<void>;
  copyFocalToClipboard(opts?: ExportFocalOptions): Promise<void>;
}
